import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { staffForgotPassword } from "@/lib/staff-api";
import { StaffAuthCard, inputClass, linkClass, primaryButtonClass } from "./StaffAuthCard";

const forgotSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type ForgotFormData = z.infer<typeof forgotSchema>;

export function StaffForgotPasswordPage(): React.ReactElement {
  const forgotMutation = useMutation({ mutationFn: (email: string) => staffForgotPassword(email) });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotFormData>({ resolver: zodResolver(forgotSchema) });

  const onSubmit = (data: ForgotFormData): void => {
    forgotMutation.mutate(data.email);
  };

  const backToSignIn = (
    <p className="mt-4 text-center text-sm">
      <Link to="/staff/login" className={linkClass}>
        Back to sign in
      </Link>
    </p>
  );

  // Success only. The server answers 200 whether or not the address has an
  // account, so an error here is a real failure and nothing was sent.
  if (forgotMutation.isSuccess) {
    return (
      <StaffAuthCard title="Forgot password">
        <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          If a staff account exists with that email, you'll receive a link to set a new password.
        </div>
        {backToSignIn}
      </StaffAuthCard>
    );
  }

  return (
    <StaffAuthCard
      title="Forgot password"
      subtitle="Enter your staff email and we'll send you a link to set a new password."
    >
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
            Email
          </label>
          <input id="email" type="email" autoComplete="email" {...register("email")} className={inputClass} />
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
        </div>

        {forgotMutation.isError && (
          <p role="alert" className="text-sm text-red-600">
            We could not send the link. Please try again in a moment.
          </p>
        )}

        <button type="submit" disabled={forgotMutation.isPending} className={primaryButtonClass}>
          {forgotMutation.isPending ? "Sending..." : "Send link"}
        </button>
      </form>
      {backToSignIn}
    </StaffAuthCard>
  );
}
